import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, User, Mail, Shield, Lock, Save } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import Seo from '../components/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProfilePage = () => {
  const { user, token, loading: authLoading, updateUser } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) navigate('/login', { replace: true });
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) setName(user.name || '');
  }, [user]);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Name cannot be empty');
      return;
    }
    setSavingProfile(true);
    try {
      const res = await axios.put(
        `${API}/auth/profile`,
        { name: name.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      updateUser({ name: res.data.name });
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setChangingPassword(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success('Password changed — use your new password next time you sign in');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  if (authLoading || !user) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center" data-testid="profile-loading">
        <Loader2 size={32} className="animate-spin text-[#B5FF00]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-16" data-testid="profile-page">
      <Seo title="Profile" description="Update your Unight profile — change your display name or reset your password." path="/profile" noindex />
      <div className="container mx-auto px-4 md:px-8 max-w-3xl">
        <div className="mb-12">
          <span className="font-mono text-xs tracking-widest text-[#B5FF00] mb-2 block">YOUR ACCOUNT</span>
          <h1 className="font-['Josefin_Sans'] text-5xl md:text-6xl font-bold text-white">
            PROFILE
          </h1>
          <p className="text-zinc-500 mt-3 font-mono text-sm">
            Manage your name and password. Email is fixed for now — contact support to change it.
          </p>
        </div>

        {/* Account Summary */}
        <div className="border border-zinc-800 p-6 mb-10 bg-[#0A0A0A]" data-testid="profile-summary">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="flex items-center gap-3">
              <Mail size={16} className="text-[#B5FF00]" />
              <span className="text-zinc-300" data-testid="profile-email">{user.email}</span>
            </div>
            <div className="flex items-center gap-3">
              <Shield size={16} className="text-[#B5FF00]" />
              <span className="text-zinc-300">
                {user.is_admin ? 'Admin' : user.is_promoter ? 'Promoter' : 'Member'}
              </span>
            </div>
          </div>
        </div>

        {/* Update Name */}
        <section className="mb-12" data-testid="profile-name-section">
          <div className="flex items-center gap-2 mb-6">
            <User size={18} className="text-[#B5FF00]" />
            <h2 className="font-['Josefin_Sans'] text-2xl font-bold text-white">DISPLAY NAME</h2>
          </div>
          <form onSubmit={handleSaveProfile} className="space-y-4">
            <div>
              <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">NAME</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full h-12 bg-[#0A0A0A] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
                required
                maxLength={80}
                data-testid="profile-name-input"
              />
            </div>
            <button
              type="submit"
              disabled={savingProfile || name.trim() === user.name}
              className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="profile-save-btn"
            >
              {savingProfile ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              SAVE
            </button>
          </form>
        </section>

        {/* Change Password */}
        <section className="border-t border-zinc-800 pt-12" data-testid="profile-password-section">
          <div className="flex items-center gap-2 mb-6">
            <Lock size={18} className="text-[#B5FF00]" />
            <h2 className="font-['Josefin_Sans'] text-2xl font-bold text-white">CHANGE PASSWORD</h2>
          </div>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">CURRENT PASSWORD</label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full h-12 bg-[#0A0A0A] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
                required
                autoComplete="current-password"
                data-testid="profile-current-password-input"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">NEW PASSWORD</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full h-12 bg-[#0A0A0A] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
                  required
                  minLength={6}
                  autoComplete="new-password"
                  data-testid="profile-new-password-input"
                />
              </div>
              <div>
                <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">CONFIRM NEW PASSWORD</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full h-12 bg-[#0A0A0A] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
                  required
                  minLength={6}
                  autoComplete="new-password"
                  data-testid="profile-confirm-password-input"
                />
              </div>
            </div>
            <p className="font-mono text-xs text-zinc-500">Minimum 6 characters.</p>
            <button
              type="submit"
              disabled={changingPassword}
              className="btn-primary flex items-center gap-2"
              data-testid="profile-change-password-btn"
            >
              {changingPassword ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />}
              UPDATE PASSWORD
            </button>
          </form>
        </section>
      </div>
    </div>
  );
};

export default ProfilePage;
